/**
 * StudyOrganizer Module Types
 */

// Enrollment status options
export const ENROLLMENT_STATUSES = [
  { value: 'startup', label: 'Startup', color: 'yellow' },
  { value: 'enrolling', label: 'Enrolling', color: 'green' },
  { value: 'closed_to_enrollment', label: 'Closed to Enrollment', color: 'gray' },
  { value: 'completed', label: 'Completed', color: 'blue' },
  { value: 'terminated', label: 'Terminated', color: 'red' },
] as const;

export type EnrollmentStatus = typeof ENROLLMENT_STATUSES[number]['value'];

// Study phase options
export const STUDY_PHASES = [
  { value: 'Phase 1', label: 'Phase 1' },
  { value: 'Phase 2', label: 'Phase 2' },
  { value: 'Phase 3', label: 'Phase 3' },
  { value: 'Phase 4', label: 'Phase 4' },
  { value: 'Phase 1/2', label: 'Phase 1/2' },
  { value: 'Phase 2/3', label: 'Phase 2/3' },
] as const;

// Bookmark types
export const BOOKMARK_TYPES = [
  { value: 'TMF', label: 'TMF', icon: 'FolderOpen' },
  { value: 'IRB', label: 'IRB', icon: 'Shield' },
  { value: 'EDC', label: 'EDC', icon: 'Database' },
  { value: 'IRT', label: 'IRT', icon: 'Package' },
  { value: 'Laboratory', label: 'Laboratory', icon: 'FlaskConical' },
  { value: 'eCOA', label: 'eCOA', icon: 'ClipboardList' },
  { value: 'ECG', label: 'ECG', icon: 'HeartPulse' },
  { value: 'eConsent', label: 'eConsent', icon: 'FileSignature' },
  { value: 'Training', label: 'Training', icon: 'GraduationCap' },
  { value: 'Payment', label: 'Payment', icon: 'CreditCard' },
  { value: 'Recruitment', label: 'Recruitment', icon: 'Users' },
  { value: 'CTMS', label: 'CTMS', icon: 'LayoutDashboard' },
  { value: 'Other', label: 'Other', icon: 'Link' },
] as const;

export type BookmarkType = typeof BOOKMARK_TYPES[number]['value'];

// Team member roles
export const TEAM_ROLES = [
  { value: 'Principal Investigator', label: 'Principal Investigator' },
  { value: 'Sub-Investigator', label: 'Sub-Investigator' },
  { value: 'Lead Coordinator', label: 'Lead Coordinator' },
  { value: 'Backup Coordinator', label: 'Backup Coordinator' },
  { value: 'Regulatory Coordinator', label: 'Regulatory Coordinator' },
  { value: 'Research Nurse', label: 'Research Nurse' },
  { value: 'Other', label: 'Other' },
] as const;

// External contact roles
export const CONTACT_ROLES = [
  { value: 'CRA', label: 'CRA (Clinical Research Associate)' },
  { value: 'Medical Monitor', label: 'Medical Monitor' },
  { value: 'Sponsor Contact', label: 'Sponsor Contact' },
  { value: 'Lab Contact', label: 'Lab Contact' },
  { value: 'Vendor Contact', label: 'Vendor Contact' },
  { value: 'Other', label: 'Other' },
] as const;

// Blinding status options
export const BLINDING_STATUSES = [
  { value: 'Blinded', label: 'Blinded' },
  { value: 'Unblinded', label: 'Unblinded' },
] as const;

// Protocol/Study type
export interface Protocol {
  id: string;
  site_id: string;
  protocol_number: string;
  sponsor_name: string | null;
  study_nickname: string | null;
  indication: string | null;
  study_phase: string | null;
  enrollment_status: EnrollmentStatus;
  status: 'active' | 'inactive' | 'completed';
  created_at: string;
  updated_at: string;
  // Extended fields from API
  site_name?: string;
  site_code?: string;
  network_id?: string;
  network_name?: string;
  team_count?: number;
  bookmark_count?: number;
  contact_count?: number;
  // Bookmarks included for list view (up to 15)
  bookmarks?: StudyBookmark[];
  // Team members included for list view
  team_members?: Array<{
    id: string;
    user_id: string;
    role: string;
    blinding_status: string | null;
    user: {
      id: string;
      email: string;
      full_name: string;
    } | null;
  }>;
  // External contacts included for list view
  contacts?: Array<{
    id: string;
    first_name: string;
    last_name: string;
    role: string;
    email: string | null;
    phone: string | null;
    company: string | null;
  }>;
}

// Study Team Member type
export interface StudyTeamMember {
  id: string;
  protocol_id: string;
  user_id: string;
  role: string;
  blinding_status: string | null;
  status: 'active' | 'inactive';
  added_by: string | null;
  created_at: string;
  updated_at: string;
  user?: {
    id: string;
    email: string;
    full_name: string;
  };
}

// Study Bookmark type
export interface StudyBookmark {
  id: string;
  protocol_id: string;
  bookmark_type: BookmarkType;
  label: string;
  url: string;
  favicon_url: string | null;
  display_order: number;
  is_hidden_default: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// Per-link portal-access assignment ("this user has been granted access to
// the portal this link points at"). Additive metadata — never gates
// visibility. Consumed by Operations Insights.
export interface BookmarkAssignment {
  id: string;
  bookmark_id: string;
  user_id: string;
  granted_at: string;
  note: string | null;
  user?: { id: string; full_name: string; email?: string | null } | null;
}

// External Contact type
export interface ExternalContact {
  id: string;
  protocol_id: string;
  first_name: string;
  last_name: string;
  role: string;
  email: string | null;
  phone: string | null;
  secondary_phone: string | null;
  company: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// User Starred Study type
export interface StarredStudy {
  protocol_id: string;
  starred_at: string;
}

// Site User type (for team member dropdown)
export interface SiteUser {
  id: string;
  email: string;
  full_name: string;
}

// Filter state
export interface StudyFilters {
  search: string;
  siteId: string | null;
  statuses: EnrollmentStatus[];
  showStarredOnly: boolean;
  showArchived: boolean;
}

// Context state
export interface StudyOrganizerState {
  protocols: Protocol[];
  starredProtocolIds: Set<string>;
  loading: boolean;
  error: string | null;
  filters: StudyFilters;
  selectedProtocol: Protocol | null;
}

// Form data types
export interface AddTeamMemberFormData {
  user_id: string;
  role: string;
  blinding_status?: string;
}

export interface AddBookmarkFormData {
  bookmark_type: BookmarkType;
  label: string;
  url: string;
}

export interface AddContactFormData {
  first_name: string;
  last_name: string;
  role: string;
  email?: string;
  phone?: string;
  secondary_phone?: string;
  company?: string;
  notes?: string;
}

export interface AddProtocolFormData {
  site_id: string;
  protocol_number: string;
  sponsor_name?: string;
  study_nickname?: string;
  indication?: string;
  study_phase?: string;
  enrollment_status?: EnrollmentStatus;
}
